import { useEffect, useState } from 'react';

const ATTR = 'data-sv2-theme';

/**
 * Is the sv2 dark theme currently on?
 *
 * OBSERVES the attribute rather than calling `useSv2Theme`, and that distinction
 * matters: `useSv2Theme` REFCOUNTS a claim on the theme and drops the attribute
 * when the last claimant unmounts. A legacy component that only wants to *read*
 * the theme — a Stripe Elements form, say — would then be holding the whole
 * shell dark for as long as it happens to be mounted, and would strobe it back
 * to light when it went away.
 *
 * Exists because some things cannot read a CSS variable at all. Stripe mounts
 * its card fields in cross-origin iframes, so their colours have to be passed as
 * JS literals and re-passed whenever the theme flips; a MutationObserver is what
 * turns "the theme changed" into a re-render for those.
 */
export const useIsSv2Dark = (): boolean => {
	const [isDark, setIsDark] = useState(
		() =>
			typeof document !== 'undefined' &&
			document.documentElement.getAttribute(ATTR) === 'dark'
	);

	useEffect(() => {
		const root = document.documentElement;
		const sync = () => setIsDark(root.getAttribute(ATTR) === 'dark');
		// The attribute can already have changed between the initial state and
		// this effect running, so read once before observing.
		sync();
		const observer = new MutationObserver(sync);
		observer.observe(root, { attributes: true, attributeFilter: [ATTR] });
		return () => observer.disconnect();
	}, []);

	return isDark;
};
